import React, { useState, useCallback, useEffect } from "react";
import LoginScreen from "./components/LoginScreen";
import Header from "./components/Header";
import Footer from "./components/Footer";
import FileUpload from "./components/FileUpload";
import FormatFixPanel from "./components/FormatFixPanel";
import ConfirmDialog from "./components/ConfirmDialog";
import DataTable from "./components/DataTable";
import ConflictTable from "./components/ConflictTable";
import ProcessingControls from "./components/ProcessingControls";
import SettingsPage from "./components/SettingsPage";
import TeacherConflictAnalysis from "./components/TeacherConflictAnalysis";
import NameResolutionSection from "./components/NameResolutionSection";
import CourseReportPanel from "./components/CourseReportPanel";

/*
 * TEMPORARY DEVELOPMENT FEATURE - SAMPLE FILE DOWNLOAD:
 *
 * Location: Header.js - "Try Sample" button
 * Purpose: Allows users to download sample.xlsx for testing the application
 * Files: /public/sample/sample.xlsx, Header.js, Header.css
 *
 * TO REMOVE THIS FEATURE:
 * 1. Delete the "Try Sample" button from Header.js
 * 2. Remove .sample-button CSS from Header.css
 * 3. Delete /public/sample/ directory
 * 4. Remove this comment block
 */

/*
 * CRITICAL ROOM ALLOCATION FIELD CONSISTENCY REQUIREMENT:
 *
 * This application maintains TWO room allocation fields for backward compatibility:
 * - room_allocation: Primary field used by web table display and Excel export
 * - room_auto_allo: Legacy field used by internal allocation logic
 *
 * BOTH FIELDS MUST BE UPDATED TOGETHER when room assignments change to ensure:
 * 1. Web table shows updated room assignments immediately
 * 2. Downloaded Excel files contain all conflict resolutions
 * 3. All functionality works consistently across the application
 *
 * Key locations that update room assignments:
 * - Auto allocation: roomAllocation.js autoAllocateRooms()
 * - Conflict resolution: App.js handleResolveConflicts()
 * - Clear functions: roomAllocation.js clearAutoAllocation()
 */
import { parseExcelFile, generateExcelFile, readWorkbookRows } from "./utils/excelUtils";
import { detectFormatIssues, groupIssues } from "./utils/excelFormatDetector";
import { applySettingsFixes, buildFixedWorkbook } from "./utils/excelFormatFixer";
import {
  assertReadableWorkbook,
  canWriteInPlace,
  buildWorkbookWithAllResults,
  countUnsavedResults,
  describeReadError,
  describeWriteError,
  ensureWritePermission,
  overwriteWithRetry,
  readFileFromDisk,
  WORKBOOK_REWRITE_NOTE,
} from "./utils/excelWriteBack";
import { checkDocument } from "./utils/courseDocCheck";
import { planCourseNameFixes } from "./utils/courseFormatFix";
import { buildCheckReport, buildFixReport } from "./utils/courseReport";
import { editWorkbookCells } from "./utils/xlsxSurgery";
import {
  autoAllocateRooms,
  autoAllocatePendingRooms,
  summarizeAllocation,
  checkDuplicateAllocations,
  findUnallocatedRooms,
  clearAutoAllocation,
  clearAllColumns,
  updateRoomConfiguration,
  resetConflictNumbersForNewFile,
} from "./utils/roomAllocation";
import { loadRoomSettings, saveRoomSettings } from "./utils/roomSettings";
import { loadExcelSettings } from "./utils/excelSettings";
import { isAuthenticated } from "./utils/authUtils";
import { logAction } from "./utils/logUtils";
import "./App.css";

/**
 * Explain why an action can't run, or return null when it can.
 * @param {Array} data - parsed rows from the uploaded file
 * @param {boolean} requiresRooms - true for actions that assign rooms
 */
const describeBlockedAction = (data, requiresRooms) => {
  if (!data.length) {
    return "No Excel data is loaded. Upload a file first.";
  }

  if (data.every((row) => row.is_section_header)) {
    return "The file only produced section headers - no data rows were read. Check the Starting Row in Settings > Excel Settings.";
  }

  if (requiresRooms && loadRoomSettings().length === 0) {
    return "No rooms are configured. Add rooms in Settings > Room Settings before allocating.";
  }

  return null;
};

// Longest run of Excel row numbers printed before the rest are summarised
const MAX_ROWS_LISTED = 12;

/**
 * Turn an allocation pass into a status message that says what was skipped,
 * with the Excel row numbers behind each reason.
 * @param {Array} data - rows after allocation
 * @param {string} intro - leading sentence for this action
 */
const describeAllocationResult = (data, intro) => {
  const { allocated, skipped, reasons } = summarizeAllocation(data);
  const lines = [intro, `${allocated} allocated, ${skipped} skipped.`];

  reasons.forEach(({ reason, rows }) => {
    lines.push("");
    lines.push(`${rows.length} skipped — ${reason}`);

    const shown = rows.slice(0, MAX_ROWS_LISTED).join(", ");
    const remaining = rows.length - MAX_ROWS_LISTED;
    lines.push(
      remaining > 0
        ? `Excel rows ${shown} and ${remaining} more`
        : `Excel row${rows.length === 1 ? "" : "s"} ${shown}`
    );
  });

  return lines.join("\n");
};

/**
 * Why Fix Format could not write, for a workbook the app holds no writable handle to.
 * @param {string} fileName - the open workbook's name
 */
const describeFixFormatBlocked = (fileName) =>
  [
    `Cannot write into ${fileName} directly, so nothing was changed.`,
    "Re-open the workbook with the Choose file button (not by dragging it in),",
    "then click Fix Format again.",
  ].join("\n");

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentView, setCurrentView] = useState("main"); // "main", "settings", or "teacher-conflicts"
  const [data, setData] = useState([]);
  const [headerInfo, setHeaderInfo] = useState({});
  const [expectedHeaders, setExpectedHeaders] = useState([]);
  const [formatReport, setFormatReport] = useState(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [sourceHandle, setSourceHandle] = useState(null);
  const [isFixingFormat, setIsFixingFormat] = useState(false);
  const [dialog, setDialog] = useState(null);
  // Dialogs the user chose to skip for the rest of this session
  const [skippedDialogs, setSkippedDialogs] = useState({});
  const [fileName, setFileName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  // One panel shared by Check Doc and Fix Format.
  const [courseReport, setCourseReport] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [unallocatedRooms, setUnallocatedRooms] = useState([]);
  const [message, setMessage] = useState("");
  const [roomSettings, setRoomSettings] = useState([]);
  const [showTeacherConflicts, setShowTeacherConflicts] = useState(false);

  // Check authentication status on component mount and load room settings
  useEffect(() => {
    if (isAuthenticated()) {
      setIsLoggedIn(true);
    }

    // Load room settings from localStorage or defaults
    const loadedSettings = loadRoomSettings();
    setRoomSettings(loadedSettings);
    updateRoomConfiguration(loadedSettings);
  }, []);

  const handleLogin = (user) => {
    logAction("Login");
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    logAction("Logout");
    setIsLoggedIn(false);
    // Clear all data when logging out
    setData([]);
    setHeaderInfo({});
    setExpectedHeaders([]);
    setFormatReport(null);
    setCourseReport(null);
    setSourceFile(null);
    setSourceHandle(null);
    setFileName("");
    setConflicts([]);
    setUnallocatedRooms([]);
    setMessage("");
    setShowTeacherConflicts(false); // Reset teacher conflicts section on logout
  };

  const resetProject = () => {
    console.log("🔄 Resetting project state for new upload...");
    setData([]);
    setHeaderInfo({});
    setExpectedHeaders([]);
    setFormatReport(null);
    setCourseReport(null);
    setSourceFile(null);
    setSourceHandle(null);
    setFileName("");
    setConflicts([]);
    setUnallocatedRooms([]);
    setMessage("");
    setShowTeacherConflicts(false); // Reset teacher conflicts section to collapsed state
    // Clear any visual update indicators
    const updatedRooms = document.querySelectorAll(".recently-updated-room");
    updatedRooms.forEach((room) => {
      room.classList.remove("recently-updated-room");
    });
    console.log("✅ Project state reset complete");
  };

  const handleUpdateRoomSettings = useCallback(
    (newRoomSettings) => {
      try {
        // Save to localStorage
        saveRoomSettings(newRoomSettings);

        // Update local state
        setRoomSettings(newRoomSettings);

        // Update the room allocation configuration
        updateRoomConfiguration(newRoomSettings);

        // Show success message temporarily
        setMessage("Room settings updated successfully! Changes will apply to new allocations.");
        setTimeout(() => {
          if (currentView === "settings") {
            setMessage("");
          }
        }, 3000);
      } catch (error) {
        console.error("Failed to update room settings:", error);
        setMessage("Error updating room settings. Please try again.");
      }
    },
    [currentView],
  );

  const handleOpenSettings = () => {
    setCurrentView("settings");
    setMessage(""); // Clear any existing messages
  };

  const handleBackToMain = () => {
    setCurrentView("main");
    setMessage(""); // Clear any existing messages
    setShowTeacherConflicts(false); // Hide teacher conflicts when going back to main
  };

  const handleToggleTeacherConflicts = () => {
    setShowTeacherConflicts(!showTeacherConflicts);
    setMessage(""); // Clear any existing messages
  };

  /**
   * Load a workbook: report any format problems first, then try to parse it.
   * @returns {Promise<{ok: boolean, formatIssues: number}>} ok is false when parsing failed;
   *   formatIssues counts the ways the file differs from Settings
   */
  const loadWorkbook = useCallback(async (file, handle) => {
    setIsProcessing(true);
    setSourceFile(file);
    setSourceHandle(handle || null);

    let report = null;
    try {
      const { rows } = await readWorkbookRows(file);
      report = detectFormatIssues(rows, loadExcelSettings());
      setFormatReport(report && report.issues.length > 0 ? report : null);
    } catch (error) {
      setFormatReport(null); // unreadable file; the parse error below explains it
    }
    const formatIssues = report ? report.issues.length : 0;

    try {
      setMessage("Parsing Excel file...");
      const result = await parseExcelFile(file);
      setData(result.data);
      setHeaderInfo(result.headerInfo);
      setExpectedHeaders(result.expectedHeaders);
      setFileName(file.name);
      setMessage(
        formatIssues > 0
          ? "File uploaded successfully, but its format does not match your Settings."
          : "File uploaded successfully!"
      );
      return { ok: true, formatIssues };
    } catch (error) {
      setMessage(`Error parsing file: ${error.message}`);
      return { ok: false, formatIssues };
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleFileUpload = useCallback(
    async (file, handle) => {
      logAction(`File Upload: ${file.name}`);
      resetProject();
      resetConflictNumbersForNewFile();
      await loadWorkbook(file, handle);
    },
    [loadWorkbook]
  );

  // Read the workbook again from where it was opened and start over from it, exactly as
  // if it had just been uploaded, so edits saved to the file since then show up.
  // Called from the dialog's confirm button: the read is the first await after that click,
  // which keeps the user activation a lapsed read permission would need.
  const reloadNow = useCallback(async () => {
    const handle = sourceHandle;
    const name = sourceFile ? sourceFile.name : fileName;

    let file;
    try {
      file = await readFileFromDisk(handle);
    } catch (error) {
      // Nothing has been reset yet, so the table still shows the earlier copy
      setMessage(
        `Error reloading the file - nothing on screen has changed. ${describeReadError(error, name)}`
      );
      return;
    }

    logAction(`Reload File: ${file.name}`);
    resetProject();
    resetConflictNumbersForNewFile();
    const { ok, formatIssues } = await loadWorkbook(file, handle);

    if (ok) {
      const savedAt = new Date(file.lastModified).toLocaleString();
      setMessage(
        formatIssues > 0
          ? `Reloaded ${file.name} as saved at ${savedAt}, but its format does not match your Settings.`
          : `Reloaded ${file.name} as saved at ${savedAt}.`
      );
    }
  }, [sourceHandle, sourceFile, fileName, loadWorkbook]);

  // Ask first: a reload replaces everything on screen, including work not yet saved
  const handleReload = useCallback(() => {
    if (!sourceHandle) {
      setMessage(
        canWriteInPlace()
          ? "Reload needs the workbook opened with the Choose file button. Open it that way once and Reload from File will work from then on."
          : "This browser cannot read a file again from disk - use Chrome or Edge over HTTPS, or upload the file again."
      );
      return;
    }

    if (skippedDialogs.reload) {
      reloadNow();
      return;
    }

    setDialog({
      title: `Reload ${sourceFile ? sourceFile.name : fileName} from disk?`,
      lines: [
        "Everything on screen is replaced with what the file holds now: the table, room allocations, conflict results and standardised names.",
        "Anything not yet written with Save All Results into File will be lost.",
        "Edits made in Excel show up only once they have been saved there.",
      ],
      confirmLabel: "Reload",
      tone: "warning",
      rememberLabel: "Don't ask again while this page is open",
      onConfirm: ({ remember }) => {
        if (remember) setSkippedDialogs((current) => ({ ...current, reload: true }));
        setDialog(null);
        reloadNow();
      },
    });
  }, [sourceHandle, sourceFile, fileName, skippedDialogs, reloadNow]);

  // Apply the fixes. Called from the dialog's confirm button, or directly when
  // only Settings change and there is nothing to confirm.
  const runFormatFix = useCallback(async () => {
    if (!formatReport || !sourceFile) return;

    const grouped = groupIssues(formatReport.issues);
    const notes = [];
    setIsFixingFormat(true);

    try {
      if (grouped.settings.length > 0) {
        const { applied } = applySettingsFixes(grouped.settings);
        notes.push(...applied);
      }

      let fileToLoad = sourceFile;

      if (grouped.file.length > 0) {
        const canOverwrite = await ensureWritePermission(sourceHandle);

        if (!canOverwrite) {
          // Leave the workbook alone rather than dropping a second copy in Downloads.
          // The Settings-only fixes above have still been applied.
          notes.push(
            `Could not write into ${sourceFile.name}, so the file itself was left unchanged - ` +
              "re-open it with the Choose file button and run this again"
          );
        } else {
          const { buffer, changes } = await buildFixedWorkbook(
            sourceFile,
            grouped.file,
            formatReport.detected.headerRow
          );
          notes.push(...changes);

          await overwriteWithRetry(sourceHandle, buffer);
          notes.push(`Saved over ${sourceFile.name}`);

          fileToLoad = new File([buffer], sourceFile.name, { type: sourceFile.type });
        }
      }

      logAction("Fix Excel Format");
      const { ok } = await loadWorkbook(fileToLoad, sourceHandle);

      if (ok) {
        setMessage(
          ["Format fixed."].concat(notes.map((note) => `• ${note}`)).join(String.fromCharCode(10))
        );
      }
    } catch (error) {
      setMessage(`Error fixing format: ${describeWriteError(error, "Fix Excel Format")}`);
    } finally {
      setIsFixingFormat(false);
    }
  }, [formatReport, sourceFile, sourceHandle, loadWorkbook]);

  // Ask first when the Excel file itself has to be rewritten
  const handleFixFormat = useCallback(() => {
    if (!formatReport || !sourceFile) return;

    const grouped = groupIssues(formatReport.issues);
    if (grouped.file.length === 0 || skippedDialogs.fixFormat) {
      runFormatFix(); // Settings only, or the user opted out of the warning
      return;
    }

    setDialog({
      title: "Fix the format in the Excel file?",
      lines: [
        `${grouped.file.length} change${
          grouped.file.length === 1 ? "" : "s"
        } need writing into ${sourceFile.name}.`,
        grouped.settings.length > 0
          ? `${grouped.settings.length} further issue${
              grouped.settings.length === 1 ? "" : "s"
            } are corrected in Settings and touch no file.`
          : "Everything else is already correct.",
        sourceHandle
          ? "Your browser may ask once for permission to edit this file."
          : "This file was not opened through Choose file, so a fixed copy will be downloaded instead.",
      ],
      note: WORKBOOK_REWRITE_NOTE,
      confirmLabel: "Fix the file",
      tone: "warning",
      rememberLabel: "Don't ask again while this page is open",
      onConfirm: ({ remember }) => {
        if (remember) setSkippedDialogs((current) => ({ ...current, fixFormat: true }));
        setDialog(null);
        runFormatFix();
      },
    });
  }, [formatReport, sourceFile, sourceHandle, runFormatFix, skippedDialogs]);

  const handleAutoAllocate = useCallback(() => {
    const blocked = describeBlockedAction(data, true);
    if (blocked) {
      setMessage(blocked);
      return;
    }
    logAction("Auto Allocate Rooms");

    try {
      setIsProcessing(true);
      setMessage("Clearing existing allocations before assigning new ones...");

      // First clear existing allocations
      const clearedData = clearAutoAllocation(data);

      // Then perform auto allocation on the cleared data
      setMessage("Allocating rooms...");
      const updatedData = autoAllocateRooms(clearedData);

      setData(updatedData);
      setMessage(
        describeAllocationResult(
          updatedData,
          "Room allocation complete. Existing allocations were cleared first."
        )
      );
    } catch (error) {
      setMessage(`Error during allocation: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [data]);

  const handleAutoAllocatePending = useCallback(() => {
    const blocked = describeBlockedAction(data, true);
    if (blocked) {
      setMessage(blocked);
      return;
    }
    logAction("Auto Allocate (Only Pending)");

    try {
      setIsProcessing(true);
      setMessage("Allocating pending rooms...");

      const updatedData = autoAllocatePendingRooms(data);
      setData(updatedData);
      setMessage(describeAllocationResult(updatedData, "Pending room allocation complete."));
    } catch (error) {
      setMessage(`Error during pending allocation: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [data]);

  const handleCheckDuplicates = useCallback(() => {
    const blocked = describeBlockedAction(data, false);
    if (blocked) {
      setMessage(blocked);
      return;
    }

    try {
      setIsProcessing(true);
      setMessage("Checking for duplicate allocations...");

      const duplicates = checkDuplicateAllocations(data, [], fileName);
      setConflicts(duplicates);
      logAction(`Check Duplicates-${duplicates.length}`);

      if (duplicates.length > 0) {
        setMessage(`Found ${duplicates.length} conflicts. Check the conflicts table below.`);
      } else {
        setMessage("No duplicate room allocations found.");
      }
    } catch (error) {
      setMessage(`Error checking duplicates: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [data]);

  const handleFindUnallocated = useCallback(() => {
    const blocked = describeBlockedAction(data, false);
    if (blocked) {
      setMessage(blocked);
      return;
    }
    logAction("Find Available Rooms");

    try {
      setIsProcessing(true);
      setMessage("Finding unallocated rooms...");

      const unallocated = findUnallocatedRooms(data);
      setUnallocatedRooms(unallocated);
      setMessage(`Found ${unallocated.length} unallocated time slots.`);
    } catch (error) {
      setMessage(`Error finding unallocated rooms: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [data]);

  const handleClearAutoAllocation = useCallback(() => {
    const blocked = describeBlockedAction(data, false);
    if (blocked) {
      setMessage(blocked);
      return;
    }
    logAction("Clear Auto Allocation");

    try {
      const updatedData = clearAutoAllocation(data);
      setData(updatedData);
      setMessage("Auto-allocation cleared successfully!");
    } catch (error) {
      setMessage(`Error clearing allocation: ${error.message}`);
    }
  }, [data]);

  const handleClearAllColumns = useCallback(() => {
    const blocked = describeBlockedAction(data, false);
    if (blocked) {
      setMessage(blocked);
      return;
    }
    logAction("Clear All Analysis");

    try {
      const updatedData = clearAllColumns(data);
      setData(updatedData);
      setConflicts([]);
      setUnallocatedRooms([]);
      setMessage("All analysis columns cleared successfully!");
    } catch (error) {
      setMessage(`Error clearing columns: ${error.message}`);
    }
  }, [data]);

  // Do the write. Called straight from the dialog's confirm button so the click
  // still counts as user activation when the permission request goes out.
  const saveAllocationsNow = useCallback(async () => {
    // Saving means saving - it must land in the workbook the user opened. If it cannot,
    // say so and let them fix it, rather than quietly leaving a second copy in Downloads
    // that they then have to reconcile by hand. "Download Updated Excel File" is the
    // button for wanting a copy.
    const canOverwrite = await ensureWritePermission(sourceHandle);
    if (!canOverwrite) {
      setMessage(
        [
          `Cannot write into ${sourceFile.name}, so nothing was saved.`,
          canWriteInPlace()
            ? "Re-open the workbook with the Choose file button, then click Save All Results into File again."
            : "This browser cannot save into a file directly - use Chrome or Edge over HTTPS, or use Download Updated Excel File.",
        ].join(String.fromCharCode(10))
      );
      return;
    }

    setIsProcessing(true);
    try {
      const { buffer, written, cleared, names } = await buildWorkbookWithAllResults(
        sourceFile,
        data
      );

      const summary =
        `${written} room${written === 1 ? "" : "s"} written, ${cleared} cleared` +
        // A count here would mislead: every name is written, not just the edited ones,
        // so "181 names saved" after standardising two would read as 181 changes.
        (names > 0 ? ", teacher names saved" : "");

      await overwriteWithRetry(sourceHandle, buffer);
      setSourceFile(new File([buffer], sourceFile.name, { type: sourceFile.type }));
      setMessage(`Saved into ${sourceFile.name}: ${summary}.`);

      // Counts only - teacher names are personal data and must not reach the log.
      logAction(`Save All Results to file-${written}-${names}`);
    } catch (error) {
      setMessage(
        `Nothing was saved. ${describeWriteError(error, "Save All Results into File")}`
      );
    } finally {
      setIsProcessing(false);
    }
  }, [data, sourceFile, sourceHandle]);

  // Scan the course full names and report what is wrong with them.
  //
  // Strictly read-only: it never asks for write permission and never touches the file,
  // which is why sourceHandle is not referenced here at all.
  const handleCheckDoc = useCallback(async () => {
    if (!sourceFile) {
      setMessage("Upload the workbook first.");
      return;
    }

    logAction("Check Doc");
    setIsProcessing(true);

    try {
      assertReadableWorkbook(sourceFile);
      const { rows } = await readWorkbookRows(sourceFile);
      const analysis = checkDocument(rows, { startRow: loadExcelSettings().startRow });
      const report = buildCheckReport(analysis, { fileName: sourceFile.name });

      setCourseReport({ kind: "check", ...report });
      setMessage(
        report.issues === 0
          ? `Check Doc: no problems found in ${analysis.courseRowCount} course names.`
          : `Check Doc: ${report.summary}`
      );
    } catch (error) {
      setMessage(`Error checking the document: ${describeWriteError(error, "Check Doc")}`);
    } finally {
      setIsProcessing(false);
    }
  }, [sourceFile]);

  // Write a planned set of course-name fixes into the workbook, then reload the table.
  //
  // Called only from the confirm dialog's button. That click is a fresh user activation,
  // and ensureWritePermission has to be the first await after it or the activation the
  // permission request depends on is already gone.
  const fixCourseFormatNow = useCallback(async (plan) => {
    const canOverwrite = await ensureWritePermission(sourceHandle);
    if (!canOverwrite) {
      setMessage(describeFixFormatBlocked(sourceFile.name));
      return;
    }

    logAction("Fix Format");
    setIsProcessing(true);

    try {
      const { buffer, skipped } = await editWorkbookCells(sourceFile, plan.edits);
      const skippedRefs = skipped || [];

      await overwriteWithRetry(sourceHandle, buffer);

      // Re-read from the new bytes so the table matches what is now on disk and a
      // second click does not re-apply against pre-fix content.
      await loadWorkbook(
        new File([buffer], sourceFile.name, { type: sourceFile.type }),
        sourceHandle
      );

      const report = buildFixReport(plan, {
        fileName: sourceFile.name,
        saved: true,
        skippedRefs,
      });

      setCourseReport({ kind: "fix", ...report });
      setMessage(report.summary);
    } catch (error) {
      setMessage(`Nothing was changed. ${describeWriteError(error, "Fix Format")}`);
    } finally {
      setIsProcessing(false);
    }
  }, [sourceFile, sourceHandle, loadWorkbook]);

  // Work out the course-name fixes, then say what writing them costs before doing it.
  //
  // Fix Format saves straight into the file and reloads the table from it, so anything in
  // the table not yet saved is lost. The plan is read first so the dialog can give real
  // numbers; that await is safe because the write starts from the dialog's own button.
  // No "don't ask again": the warning is the point, and there is no undo.
  const handleFixCourseFormat = useCallback(async () => {
    if (!sourceFile) {
      setMessage("Upload the workbook first.");
      return;
    }
    if (!sourceHandle) {
      setMessage(describeFixFormatBlocked(sourceFile.name));
      return;
    }

    setIsProcessing(true);
    let plan;
    let unsaved;
    try {
      assertReadableWorkbook(sourceFile);
      const { rows } = await readWorkbookRows(sourceFile);
      plan = planCourseNameFixes(rows, { startRow: loadExcelSettings().startRow });
      unsaved = countUnsavedResults(data, rows);
    } catch (error) {
      setMessage(`Nothing was changed. ${describeWriteError(error, "Fix Format")}`);
      return;
    } finally {
      setIsProcessing(false);
    }

    // Nothing to write, so nothing to confirm: show the report and leave the file alone.
    if (plan.edits.length === 0) {
      const report = buildFixReport(plan, { fileName: sourceFile.name, saved: false });
      setCourseReport({ kind: "fix", ...report });
      setMessage(report.summary);
      return;
    }

    const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
    const lost = [];
    if (unsaved.rooms > 0) lost.push(plural(unsaved.rooms, "room allocation change"));
    if (unsaved.names > 0) lost.push(plural(unsaved.names, "teacher name change"));

    setDialog({
      title: `Fix course names in ${sourceFile.name}?`,
      lines: [
        `${plural(plan.rowsChanged, "course name")} will be rewritten and saved straight into ` +
          `${sourceFile.name}. There is no undo; the report afterwards lists every change.`,
        lost.length > 0
          ? `The table has ${lost.join(" and ")} not saved in the file yet. They will be lost, ` +
            "because the table is reloaded from the file afterwards. Cancel and use Save All " +
            "Results into File first to keep them."
          : "Everything in the table is already saved in the file, so nothing on screen will be lost.",
        `Close ${sourceFile.name} in Excel first. Your browser may ask once for permission to edit it.`,
      ],
      note:
        "Only course names change: blank spaces next to ~, the module name, the cohort and the " +
        "session. Tutor names, rooms and everything else in the workbook are left exactly as " +
        "they are. Rows Fix Format cannot fix with certainty are listed, not changed.",
      confirmLabel: "Fix and save",
      tone: "warning",
      onConfirm: () => {
        setDialog(null);
        fixCourseFormatNow(plan);
      },
    });
  }, [sourceFile, sourceHandle, data, fixCourseFormatNow]);

  // Write the Room Allocated column straight back into the uploaded workbook,
  // row for row, so the file on disk matches the table.
  const handleSaveAllocationsToFile = useCallback(() => {
    const blocked = describeBlockedAction(data, false);
    if (blocked) {
      setMessage(blocked);
      return;
    }

    if (!sourceFile) {
      setMessage(
        "The original workbook is no longer open. Upload it again to save allocations into it."
      );
      return;
    }

    // The save now also carries standardised teacher names, so having no rooms yet is
    // no longer a reason to block it - the names alone are worth writing.
    const allocated = data.filter((row) => !row.is_section_header && row.room_allocation).length;
    if (allocated === 0) {
      setMessage(
        "No rooms are allocated yet. Saving will still write any standardised teacher names."
      );
    }

    // No dialog of our own here: the browser's own permission prompt is the
    // confirmation. Calling straight from the click also keeps the user
    // activation that the permission request depends on.
    saveAllocationsNow();
  }, [data, sourceFile, saveAllocationsNow]);

  const handleDownload = useCallback(() => {
    if (!data.length) {
      setMessage("No data to download");
      return;
    }
    logAction("Download Excel File");

    try {
      setIsProcessing(true);
      setMessage("Generating Excel file with all resolved conflicts...");

      console.log("📊 Excel Export: Using data:", {
        dataCount: data?.length,
        sampleData: data?.[0],
        conflictsCount: conflicts?.length,
        unallocatedCount: unallocatedRooms?.length,
      });

      generateExcelFile(data, conflicts, unallocatedRooms, fileName, headerInfo);
      setMessage("File downloaded successfully with all conflict resolutions!");
    } catch (error) {
      setMessage(`Error generating file: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [data, conflicts, unallocatedRooms, fileName]);

  const handleResolveConflicts = useCallback(
    (resolutionData) => {
      try {
        logAction("Resolve Conflicts");
        setIsProcessing(true);
        setMessage("Applying conflict resolutions...");

        // Create a copy of the data to modify
        const updatedData = [...data];
        let changesApplied = 0;
        let rowsNeedingExcelMarking = [];

        // Apply each resolution
        resolutionData.forEach(({ conflict, resolutions }) => {
          resolutions.forEach(({ row_number, new_room, changed, needsExcelMarking }) => {
            if (changed) {
              // Find the row in data using row_index (which corresponds to Excel row number)
              const dataRowIndex = updatedData.findIndex((row) => row.row_index === row_number);

              if (dataRowIndex !== -1) {
                // CRITICAL: Update BOTH room fields to ensure consistency across web table and Excel export
                updatedData[dataRowIndex].room_auto_allo = new_room;
                updatedData[dataRowIndex].room_allocation = new_room;
                updatedData[dataRowIndex].recently_updated_room = true; // Mark for visual highlighting
                changesApplied++;
                console.log(
                  `✅ Updated room allocation for row ${row_number}: ${new_room} (updated both room_auto_allo and room_allocation fields)`,
                );
              } else {
                console.error(`❌ Could not find row with row_index ${row_number} in data array`);
              }
            }

            // Track rows that need Excel marking for manual intervention
            if (needsExcelMarking) {
              rowsNeedingExcelMarking.push({
                row_number,
                conflict_room: conflict.conflict_room,
                day_time: conflict.day_time,
              });
            }
          });
        });

        // Store rows needing marking for Excel export and apply room changes
        const finalData =
          rowsNeedingExcelMarking.length > 0
            ? updatedData.map((row) => {
                const needsMarking = rowsNeedingExcelMarking.some(
                  (marked) => marked.row_number === row.row_index,
                );
                return needsMarking ? { ...row, needs_excel_marking: true } : row;
              })
            : updatedData;

        setData(finalData);

        // Re-check for conflicts after applying changes, preserving existing conflict IDs
        const newConflicts = checkDuplicateAllocations(finalData, conflicts, fileName);
        setConflicts(newConflicts);

        console.log(`Conflict resolution: ${newConflicts.length} conflicts remaining`);

        // Clear the "recently updated" markers after 5 seconds to prevent permanent highlighting
        setTimeout(() => {
          setData((prevData) =>
            prevData.map((row) => {
              const { recently_updated_room, ...cleanedRow } = row;
              return cleanedRow;
            }),
          );
        }, 5000);

        if (changesApplied > 0 || rowsNeedingExcelMarking.length > 0) {
          let message = "";
          if (changesApplied > 0) {
            message += `✅ Successfully applied ${changesApplied} room reassignments. The web table has been updated and the changes will be reflected in the downloaded Excel sheet. `;
          }
          if (rowsNeedingExcelMarking.length > 0) {
            message += `${rowsNeedingExcelMarking.length} row${
              rowsNeedingExcelMarking.length > 1 ? "s" : ""
            } will be highlighted in yellow in Excel export for manual resolution. `;
          }
          message += `${
            newConflicts.length > 0
              ? `${newConflicts.length} conflicts remaining.`
              : "All resolvable conflicts processed!"
          }`;
          setMessage(message);
        } else {
          setMessage(
            "No changes were applied. Please make sure to confirm your room selections with the OK button.",
          );
        }
      } catch (error) {
        setMessage(`Error applying resolutions: ${error.message}`);
      } finally {
        setIsProcessing(false);
      }
    },
    [data],
  );

  // If not logged in, show login screen
  if (!isLoggedIn) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // Show settings page
  if (currentView === "settings") {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Not sticky here: the Settings bar with Back to Main pins itself to the top
            instead, and two sticky bars would stack or overlap. */}
        <Header
          onLogout={handleLogout}
          onOpenSettings={handleOpenSettings}
          currentView={currentView}
          sticky={false}
        />
        <SettingsPage
          onBack={handleBackToMain}
          roomSettings={roomSettings}
          onUpdateRoomSettings={handleUpdateRoomSettings}
        />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onLogout={handleLogout}
        onOpenSettings={handleOpenSettings}
        currentView={currentView}
      />
      <div className="container mx-auto px-4 py-8">
        {/* File Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Upload Excel File</h2>
          <FileUpload
            onFileUpload={handleFileUpload}
            isProcessing={isProcessing}
            openFileName={sourceFile ? sourceFile.name : ""}
            canSaveBack={Boolean(sourceHandle)}
          />
        </div>

        <ConfirmDialog
          open={Boolean(dialog)}
          title={dialog ? dialog.title : ""}
          lines={dialog ? dialog.lines : []}
          note={dialog ? dialog.note : undefined}
          confirmLabel={dialog ? dialog.confirmLabel : "Confirm"}
          tone={dialog ? dialog.tone : "primary"}
          rememberLabel={dialog ? dialog.rememberLabel : undefined}
          onConfirm={(result) => dialog && dialog.onConfirm(result)}
          onCancel={() => setDialog(null)}
        />

        {/* Format problems found in the uploaded sheet - fixing is optional */}
        <FormatFixPanel
          report={formatReport}
          fileName={sourceFile ? sourceFile.name : fileName}
          hasFileHandle={Boolean(sourceHandle)}
          isFixing={isFixingFormat}
          onFix={handleFixFormat}
          onDismiss={() => setFormatReport(null)}
        />

        {/* Processing Controls */}
        {data.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Processing Controls</h2>
            <ProcessingControls
              onAutoAllocate={handleAutoAllocate}
              onAutoAllocatePending={handleAutoAllocatePending}
              onCheckDuplicates={handleCheckDuplicates}
              onFindUnallocated={handleFindUnallocated}
              onClearAutoAllocation={handleClearAutoAllocation}
              onClearAllColumns={handleClearAllColumns}
              onReload={handleReload}
              onSaveAllocations={handleSaveAllocationsToFile}
              onDownload={handleDownload}
              onCheckDoc={handleCheckDoc}
              onFixFormat={handleFixCourseFormat}
              isProcessing={isProcessing}
            />
          </div>
        )}

        {/* Check Doc / Fix Format report, between the controls and the table */}
        <CourseReportPanel
          report={courseReport}
          title={courseReport && courseReport.kind === "fix" ? "Fix Format" : "Check Doc"}
          onReset={() => setCourseReport(null)}
        />

        {/* Teacher Conflict Analysis Section */}
        {data.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            {!showTeacherConflicts ? (
              // Initial clean view - just title and button
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-orange-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800">
                      Teacher Conflict Analysis
                    </h2>
                    <p className="text-sm text-gray-600">
                      Find teachers booked twice in one time slot, and rooms given to two teachers
                      at once
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleToggleTeacherConflicts}
                  className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 font-medium"
                >
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  Show Teacher Conflicts
                </button>
              </div>
            ) : (
              // Expanded view with hide button and full analysis
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold text-gray-800">
                    Teacher Conflict Detection
                  </h2>
                  <button
                    onClick={handleToggleTeacherConflicts}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
                  >
                    Hide Teacher Conflicts
                  </button>
                </div>
                <TeacherConflictAnalysis
                  excelData={{ data, headerInfo, expectedHeaders }}
                  fileName={fileName}
                />
              </>
            )}
          </div>
        )}

        {/* Name Resolution Section */}
        {data.length > 0 && (
          <NameResolutionSection
            excelData={{ data, headerInfo, expectedHeaders }}
            fileName={fileName}
            onDataUpdated={(standardizedData) => {
              // Update the main data when names are standardized
              console.log("📥 App.js: Received standardized data:", {
                currentDataCount: data?.length,
                newDataCount: standardizedData.data?.length,
                sampleCurrent: data?.[0],
                sampleNew: standardizedData.data?.[0],
              });
              setData(standardizedData.data);
              console.log("✅ App.js: Data state updated");
            }}
            onNotification={(notificationMessage) => {
              // Set the main app notification message
              setMessage(notificationMessage);
            }}
          />
        )}

        {/* Status Message */}
        {message && (
          <div
            className={`p-4 rounded-lg mb-6 max-w-3xl mx-auto whitespace-pre-line leading-relaxed text-base md:text-lg font-medium shadow-lg ${
              // a multi-line report reads as a block, a one-liner as a banner
              message.includes("\n") ? "text-left" : "text-center"
            } ${
              message.includes("Error") ||
              (message.includes("conflicts") && !message.includes("Successfully"))
                ? "bg-red-100 text-red-700 border-2 border-red-400"
                : "bg-green-100 text-green-700 border-2 border-green-400"
            }`}
          >
            {message}
          </div>
        )}

        {/* Data Table */}
        {data.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            {/* Display header information */}
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Room Allocation Data ({data.length} rows)
            </h2>
            <DataTable
              data={data}
              title="Main Data"
              maxHeight="400px"
              headerInfo={headerInfo}
              expectedHeaders={expectedHeaders}
            />
          </div>
        )}

        {/* Conflicts Table */}
        {conflicts.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-2xl font-semibold text-red-600">
                Duplicate Allocations ({conflicts.length} conflicts)
              </h2>
              <button
                onClick={() => {
                  logAction("Copy Conflicts");
                  const text = conflicts
                    .map((c, i) => {
                      const itemNos = c.conflicting_data.map((d) => d.item_no).join(", ");
                      return `Conflict #${i + 1} : Time Slot: ${c.day_time} | Room: ${c.conflict_room} | Excel Rows: ${c.conflict_rows} | Item no (${itemNos})`;
                    })
                    .join("\n");
                  navigator.clipboard.writeText(text).then(() => {
                    const btn = document.getElementById("copy-conflicts-btn");
                    if (btn) {
                      btn.textContent = "Copied!";
                      setTimeout(() => (btn.textContent = "Copy Conflicts"), 1500);
                    }
                  });
                }}
                id="copy-conflicts-btn"
                className="btn btn-primary text-sm px-4 py-1.5 shadow-sm hover:shadow"
                type="button"
              >
                Copy Conflicts
              </button>
            </div>
            <ConflictTable
              conflicts={conflicts}
              data={data}
              onResolveConflicts={handleResolveConflicts}
            />
          </div>
        )}

        {/* Unallocated Rooms Table */}
        {unallocatedRooms.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">
              Unallocated Rooms ({unallocatedRooms.length} time slots)
            </h2>
            <DataTable data={unallocatedRooms} title="Unallocated" maxHeight="300px" />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default App;
